import './index.erb';
import './index.rb';
import ModalManager from 'utils/modal_manager';
import { Application, Controller } from 'utils/stimulus';

// Nomes do controller, dos atributos e dos hooks de classe: FONTE ÚNICA.
//
// O markup vive no `index.erb` e é lido daqui — duas cópias do mesmo nome, em duas linguagens.
// Nada amarrava as cópias: renomear `js-weekly-errors-title` só no ERB deixava a suíte verde e
// quebrava a feature em produção, e quebrava em SILÊNCIO, porque o esqueleto tem altura zero e
// `aria-busy` (sem exceção, sem card, sem sintoma). O `index.test.js` lê o ERB do disco e confere
// que cada nome declarado abaixo está lá — é isso que transforma o rename em teste vermelho.
export const CONTROLLER_NAME = 'weekly-errors-notebook-card';

export const ATTRS = {
  endpoint: 'data-weekly-errors-endpoint',
  userId: 'data-weekly-errors-user-id',
  originKey: 'data-weekly-errors-origin-key',
  periodLabel: 'data-weekly-errors-period-label',
  upgradeUrl: 'data-weekly-errors-upgrade-url',
  activeNotebookIds: 'data-weekly-errors-active-notebook-ids',
  source: 'data-weekly-errors-source',
  userPlan: 'data-weekly-errors-user-plan'
};

export const HOOKS = {
  eyebrow: 'js-weekly-errors-eyebrow',
  title: 'js-weekly-errors-title',
  description: 'js-weekly-errors-description',
  period: 'js-weekly-errors-period',
  cta: 'js-weekly-errors-cta',
  dismiss: 'js-weekly-errors-dismiss'
};

// O modal de paywall é um Cell IRMÃO (`modals/weekly_errors_paywall_modal`), montado nas mesmas três
// views. Não pode morar dentro deste card: o root fica com `visibility: hidden; height: 0` enquanto
// `aria-busy` é `true`, e o modal herdaria isso.
//
// O servidor só emite o markup para quem está fora do plano — quem é elegível não recebe o nó, e
// `create` nunca chega a procurá-lo.
export const PAYWALL_MODAL_SELECTOR = '#js-weekly-errors-paywall-modal';

// Amplitude. Os três primeiros são o payload que `modals/weekly_errors_paywall_modal/index.js` já
// manda — duplicado de propósito: aquele arquivo e o `not_elite_user_modal` já carregam duas cópias
// idênticas deste bloco, e extrair um utilitário compartilhado ampliaria a superfície da entrega sem
// ganho. O jest amarra as cópias.
//
// `source notebook error generated` é o enum do GERE3-7. O VALOR não mora aqui: vem do
// `data-weekly-errors-source`, que o Cell preenche a partir da view (`WeeklyErrorsNotebookCard::SOURCES`).
export const TRACKING = {
  featureName: 'Caderno de Erros da Semana',
  platform: 'portal',
  project: 'odin',
  sourceProperty: 'source notebook error generated',
  userPlanProperty: 'user plan'
};

// Fonte destas strings: `weeklyErrorsNotebook` em
// `apps/web/app/internationalization/locales/pt/questions.json` do `alfheim-mono`. É a mesma feature
// nos dois sistemas, e o aluno atravessa os dois — copy divergente faz um produto parecer dois.
//
// As 12 strings de tela batem, conferidas uma a uma extraindo os valores REAIS dos dois lados. O
// alfheim já traz o título único e o subtítulo com período; este arquivo veio atrás, não na frente.
//
// ! O que ainda diverge NÃO é copy, é comportamento de BACKEND: aqui o Raven passou a ser chamado
// para quem está fora do plano, então o card bloqueado tem contagem; lá `wrong_count` segue nulo
// para não-elegível e o subtítulo dele cai em `subtitleNoCount`. Ver `blocked_payload` no
// controller.
//
// O período aparece em TODO estado, agora no subtítulo. "essa semana", "semana passada" e "na última
// semana" continuam PROIBIDAS: a janela é a semana FECHADA ANTERIOR, e numa terça 12/05 "essa
// semana" abriria um caderno de 04 a 10/05. No domingo o erro dobra. É por isso que o parêntese
// carrega a data literal em vez de um advérbio.
//
// "automático" segue proibido: o caderno nasce do clique do aluno, não de um job.
//
// O TÍTULO é um só, nos três estados — inclusive onde o caderno ainda não existe (oferta) e onde o
// aluno não pode criá-lo (bloqueado). Foi decisão de produto, tomada sabendo disso; a regra antiga
// ("'está pronto' só quando o caderno existe") saiu junto com o teste que a guardava.
//
// O EYEBROW é um só pela mesma razão de sempre: alternar rótulos fazia o mesmo bloco parecer dois
// produtos. A caixa é a da fonte, sem `text-transform` — o design system proíbe caixa alta
// ornamental, e o `.badge` do SCSS foi limpo junto.
//
// O BOTÃO é um só: "Abrir caderno". O clique entrega isso em qualquer estado — criando antes, quando
// ainda não existe caderno (ver `create`). As três chaves seguem separadas de propósito, mesmo com o
// valor igual: colapsá-las apagaria que são três estados distintos.
export const COPY = {
  eyebrow: 'Exclusivo para você',

  // Sem interpolação: o período saiu do título e foi para o subtítulo.
  title: 'Hora da Revisão: seu caderno de erros da semana está pronto',

  // `null` = não apuramos (Raven fora). Cai na frase sem número — que é `subtitleNoCount` no
  // alfheim, adotada aqui byte a byte. Interpolar o null imprimiria "null questões para revisar" na
  // tela; sumir com a linha inteira deixaria o subtítulo só com o parêntese, que lê como legenda
  // solta. Zero chega aqui como `null`, e não como número: quem converte é o `|| null` do `copyFor`
  // — ver a razão lá.
  countLine: count =>
    typeof count === 'number'
      ? `${count} ${count === 1 ? 'questão' : 'questões'} para revisar`
      : 'Questões para revisar',

  // Em itálico, sempre presente. É o único lugar que diz DE QUE semana o caderno é: "da semana", no
  // título, não basta.
  periodLine: label => `(Semana de ${label})`,

  offerCta: 'Abrir caderno',
  readyCta: 'Abrir caderno',

  // Não é CTA: é o botão travado enquanto o POST está em voo e a navegação sai. "Abrindo" e não
  // "Criando" porque é o que o aluno pediu ao clicar — a criação é meio, não fim.
  creatingCta: 'Abrindo caderno...',

  blockedCta: 'Abrir caderno',

  errorToast: 'Não foi possível criar o caderno agora. Tente de novo.',
  emptyToast: label => `Não encontramos questões erradas de ${label}.`
};

export function dismissKey(userId, originKey) {
  return `weeklyErrorsNotebook.${userId}.${originKey}`;
}

export function isDismissed(userId, originKey) {
  try {
    return window.localStorage.getItem(dismissKey(userId, originKey)) === '1';
  } catch (e) {
    // Modo privado de alguns navegadores derruba o localStorage. Sem persistência o card
    // reaparece, que é melhor do que sumir com a feature.
    return false;
  }
}

export function markDismissed(userId, originKey) {
  try {
    window.localStorage.setItem(dismissKey(userId, originKey), '1');
  } catch (e) {
    // idem
  }
}

/**
 * Decide se o card aparece. Concentra os estados que NÃO renderizam, que são a maioria.
 *
 * `activeNotebookIds` são os cadernos já aplicados como filtro na tela: se o aluno já está dentro
 * do caderno da semana, oferecer que ele o abra é ruído.
 */
export function shouldRender(payload, activeNotebookIds) {
  if (!payload) {
    return false;
  }

  // UMA regra, dos dois lados do gate: sem erro apurado na janela não há card. `!wrong_count` cobre
  // de uma vez o `null` do Raven fora e o zero medido. O preço é que, com o Raven fora, ninguém
  // fora do plano é convidado; o ganho é os três sistemas dizerem a mesma coisa.
  if (!payload.wrong_count) {
    return false;
  }

  const hasNotebook =
    payload.notebook_id !== null && payload.notebook_id !== undefined;

  // Já está consumindo o caderno agora.
  if (
    hasNotebook &&
    (activeNotebookIds || []).indexOf(payload.notebook_id) > -1
  ) {
    return false;
  }

  return true;
}

/**
 * Textos de cada estado. Recebe o payload já aprovado por `shouldRender`.
 */
export function copyFor(payload, periodLabel, upgradeUrl) {
  // Iguais nos três estados. Só a CONTAGEM, o CTA e o destino variam daqui para baixo.
  const fixed = {
    eyebrow: COPY.eyebrow,
    title: COPY.title,
    period: COPY.periodLine(periodLabel)
  };

  if (payload.eligible === false) {
    return {
      ...fixed,
      // Os erros da janela — quem está bloqueado nunca tem caderno. Vem do Raven desde a reversão
      // da D7. Zero não chega aqui: `shouldRender` já cortou o card.
      description: COPY.countLine(payload.wrong_count),
      cta: COPY.blockedCta,
      // O card bloqueado é a superfície de conversão: um CTA com `href="#"` deixava o aluno sem
      // para onde ir. O destino já vem do servidor, bifurcado entre pagante e gratuito.
      href: upgradeUrl || null
    };
  }

  const hasNotebook =
    payload.notebook_id !== null && payload.notebook_id !== undefined;

  if (hasNotebook) {
    return {
      ...fixed,
      // `wrong_count` também aqui: este ramo lia `remaining`, que saiu do payload. O `|| null`
      // segura o zero que entra pelo repaint do POST, que não passa pelo `shouldRender`.
      description: COPY.countLine(payload.wrong_count || null),
      cta: COPY.readyCta,
      href: payload.notebook_url
    };
  }

  return {
    ...fixed,
    description: COPY.countLine(payload.wrong_count),
    cta: COPY.offerCta,
    href: null
  };
}

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : null;
}

export default class WeeklyErrorsNotebookCard extends Controller {
  connect() {
    this.creating = false;

    // Dispensado não renderiza E não faz fetch — a chamada seria trabalho jogado fora, inclusive
    // no servidor.
    if (isDismissed(this.userId, this.originKey)) {
      this.element.remove();
      return;
    }

    this.load();
  }

  get userId() {
    return this.element.getAttribute(ATTRS.userId);
  }

  get originKey() {
    return this.element.getAttribute(ATTRS.originKey);
  }

  get periodLabel() {
    return this.element.getAttribute(ATTRS.periodLabel);
  }

  get upgradeUrl() {
    return this.element.getAttribute(ATTRS.upgradeUrl);
  }

  get endpoint() {
    return this.element.getAttribute(ATTRS.endpoint);
  }

  get activeNotebookIds() {
    return (this.element.getAttribute(ATTRS.activeNotebookIds) || '')
      .split(',')
      .filter(Boolean)
      .map(Number);
  }

  get source() {
    return this.element.getAttribute(ATTRS.source) || '';
  }

  get userPlan() {
    return this.element.getAttribute(ATTRS.userPlan) || '';
  }

  // `UserTracker` é global (`utils/trackers/user` o pendura em `window` a partir do pack), mas o
  // pack pode não ter avaliado quando o primeiro clique cai — daí a guarda, a mesma do
  // `questions_limit_modal`.
  //
  // O `.emit` é conferido junto, e não só o objeto: este método roda ANTES de `openPaywall`, então
  // um TypeError aqui deixaria o aluno bloqueado clicando num botão que não abre nada. Analytics não
  // pode derrubar a superfície de conversão que ela mede.
  track(eventName) {
    if (!window.UserTracker || typeof window.UserTracker.emit !== 'function') {
      return;
    }

    window.UserTracker.emit('event', {
      eventName: eventName,
      data: {
        'feature name': TRACKING.featureName,
        'source platform': TRACKING.platform,
        'source project': TRACKING.project,
        [TRACKING.sourceProperty]: this.source,
        // Sempre presente, e nunca string vazia: o Cell resolve o rótulo do `public_level`, que
        // sempre existe para um logado — e o card só renderiza para logado (`visible?`).
        [TRACKING.userPlanProperty]: this.userPlan
      }
    });
  }

  async load() {
    let payload = null;

    try {
      const response = await fetch(this.endpoint, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });

      if (response.ok) {
        payload = await response.json();
      }
    } catch (e) {
      payload = null;
    }

    if (!shouldRender(payload, this.activeNotebookIds)) {
      this.element.remove();
      return;
    }

    this.payload = payload;
    this.paint(copyFor(payload, this.periodLabel, this.upgradeUrl));
    this.element.setAttribute('aria-busy', 'false');
  }

  paint(copy) {
    this.setText(`.${HOOKS.eyebrow}`, copy.eyebrow);
    this.setText(`.${HOOKS.title}`, copy.title);
    // Dois nós, não um: o período vai em `<em>`. Ambos por `textContent` — o itálico é markup do
    // ERB, e nenhuma string de copy chega ao DOM como HTML.
    this.setText(`.${HOOKS.description}`, copy.description);
    this.setText(`.${HOOKS.period}`, copy.period);

    const cta = this.ctaElement;

    if (!cta) {
      return;
    }

    cta.textContent = copy.cta;
    // Limpo antes do if/else: deixá-lo só no ramo com href fazia o estado depender da ordem das
    // chamadas, e um repaint sem href travaria o CTA em `pointer-events: none` sem sintoma visual.
    cta.removeAttribute('aria-disabled');
    cta.setAttribute('href', copy.href || '#');
  }

  setText(selector, value) {
    const node = this.element.querySelector(selector);

    if (node) {
      node.textContent = value;
    }
  }

  get ctaElement() {
    return this.element.querySelector(`.${HOOKS.cta}`);
  }

  // Devolve `false` quando o modal não está no DOM — e aí o clique segue como navegação normal para
  // a URL de upgrade. Sem essa saída, um aluno bloqueado numa tela onde o Cell do modal não foi
  // montado clicaria no CTA e nada aconteceria.
  openPaywall() {
    const modal = document.querySelector(PAYWALL_MODAL_SELECTOR);

    if (!modal) {
      return false;
    }

    // Devolver o foco ao CTA é trabalho nosso. O Bootstrap 3 só faz isso no handler do data-api
    // (`[data-toggle="modal"]`), que registra o `hidden.bs.modal` do lado de quem abriu — abrindo
    // por JS esse trecho não roda, e o foco cairia no `<body>`, jogando quem navega por teclado de
    // volta ao topo da página. `one` para não empilhar um handler por clique.
    const opener = this.ctaElement;

    if (window.jQuery) {
      window
        .jQuery(modal)
        .one('hidden.bs.modal', () => opener && opener.focus());
    }

    ModalManager.open(PAYWALL_MODAL_SELECTOR);

    return true;
  }

  // `data-action` do CTA. Só o estado de oferta cria: com caderno o CTA é um link normal, e
  // bloqueado ele abre o paywall.
  async create(event) {
    if (!this.payload) {
      return;
    }

    // O `href` do CTA bloqueado continua sendo a página de upgrade, de propósito: o modal é o
    // caminho preferido, a navegação é o fallback quando ele não está presente.
    if (this.payload.eligible === false) {
      // Medido no CLIQUE, não no `shown.bs.modal`: os dois não são o mesmo instante, porque
      // `openPaywall` devolve false quando o Cell do modal não está no DOM e o CTA navega direto
      // para a URL de upgrade. Medindo aqui, esse caminho também conta.
      //
      // ! O público que cai aqui NÃO é "básico ou gratuito" como o card do Nexus descreve: inclui
      // PAGANTES de níveis legados (premium, black, judicial), porque a elegibilidade é allowlist
      // sobre o nível cru. O evento significa "não elegível".
      this.track('notebook error clicked');

      if (this.openPaywall() && event) {
        event.preventDefault();
      }

      return;
    }

    // Reabrir tambem conta, como ja contava no Portal. O CTA aqui e um link normal: o evento sai
    // antes da navegacao, e o `UserTracker` sobrevive porque o Turbolinks nao recarrega o JS.
    if (this.payload.notebook_id) {
      this.track('notebook error clicked');

      return;
    }

    event.preventDefault();

    // Cliques repetidos não disparam POST novo.
    if (this.creating) {
      return;
    }

    this.creating = true;
    const cta = this.ctaElement;

    if (cta) {
      cta.textContent = COPY.creatingCta;
      cta.setAttribute('aria-disabled', 'true');
    }

    let response;

    try {
      response = await fetch(this.endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken()
        },
        body: JSON.stringify({ originType: 'resolved_period' })
      });
    } catch (e) {
      this.failed();
      return;
    }

    // 422: não havia questão errada na janela. O card sai da tela.
    if (response.status === 422) {
      // Não é erro: o aluno não errou nada naquela semana. Vermelho dizia o contrário.
      if (window.toastr) {
        window.toastr.info(COPY.emptyToast(this.periodLabel));
      }
      this.element.remove();
      return;
    }

    if (!response.ok) {
      this.failed();
      return;
    }

    const created = await response.json();

    // Antes do redirect, e só quando criou de verdade. `reused: true` quer dizer que a origem já
    // existia e NADA foi criado — contá-lo infla a métrica, e o caminho é real: duas abas abertas, a
    // primeira cria, a segunda ainda mostra a oferta e o clique dela volta reusado.
    //
    // Emitir antes de navegar é seguro: `Turbolinks.visit` não recarrega o contexto JS, então a fila
    // do `UserTracker` sobrevive e `turbolinks:render` a drena do outro lado.
    if (created.reused !== true) {
      this.track('notebook error created');
    }

    // Criar E abrir num clique só. O botão diz "Abrir caderno" também aqui, então parar no card
    // repintado seria prometer uma coisa e entregar outra: o aluno teria de clicar de novo, no
    // mesmo botão, com o mesmo texto.
    //
    // A URL vem do POST (`notebook_url` no payload de escrita) — não é preciso reler nada.
    if (created.notebook_url) {
      // `creating` fica true de propósito: durante a navegação o CTA continua travado, senão um
      // segundo clique dispararia POST enquanto a página ainda está saindo.
      this.redirectTo(created.notebook_url);
      return;
    }

    // Sem URL não há para onde ir. Repinta e deixa o CTA — que agora tem `href` — levar no clique
    // seguinte, em vez de deixar o aluno num botão morto.
    this.payload = created;
    this.creating = false;
    this.paint(copyFor(created, this.periodLabel, this.upgradeUrl));
  }

  // `notebook_url` é sempre caminho do próprio domínio (`questions_path` no controller), então não
  // existe o ramo de URL externa que `modals/setup/index.js` precisa ter.
  //
  // O Turbolinks é global: `packs/application.js` importa `utils/turbolinks`, que chama
  // `Turbolinks.start()`. A guarda cobre o pack global ainda não ter avaliado quando o clique cai.
  redirectTo(url) {
    if (window.Turbolinks && typeof window.Turbolinks.visit === 'function') {
      window.Turbolinks.visit(url);
      return;
    }

    window.location.href = url;
  }

  failed() {
    this.creating = false;

    if (window.toastr) {
      window.toastr.error(COPY.errorToast);
    }

    const cta = this.ctaElement;

    if (cta) {
      cta.textContent = COPY.offerCta;
      cta.removeAttribute('aria-disabled');
    }
  }

  dismiss(event) {
    if (event) {
      event.preventDefault();
    }

    // ANTES do `remove()`: `track` lê `this.source` e `this.userPlan` do próprio elemento.
    //
    // ! A UMA PALAVRA de `notebook error modal dismissed`, que o paywall emite: aquele é "viu o
    // preço e foi embora", este é "recusou antes de ver". Trocar um pelo outro não quebra nada.
    this.track('notebook error banner dismissed');

    markDismissed(this.userId, this.originKey);
    this.element.remove();
  }
}

global.WeeklyErrorsNotebookCardApplication =
  global.WeeklyErrorsNotebookCardApplication || Application.start();
global.WeeklyErrorsNotebookCardApplication.register(
  CONTROLLER_NAME,
  WeeklyErrorsNotebookCard
);
